import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useFormContext } from '../../context/FormContext';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

const { FiUploadCloud, FiFileText, FiCheckCircle, FiDownload, FiInfo } = FiIcons;

export const WorkbookUpload = () => {
  const { data, updateData, submitForm } = useFormContext();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = (file) => {
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      updateData('upload', 'workbook', file);
    } else {
      alert('Please upload a valid .xlsx file.');
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.target.files ? e.target.files[0] : e.dataTransfer.files[0]);
  };

  const missing = [];
  if (!data.contact.firstName) missing.push('first name');
  if (!data.contact.lastName) missing.push('last name');
  if (!data.contact.email) missing.push('email');
  if (!data.upload.workbook) missing.push('completed workbook');

  const canSubmit = missing.length === 0;

  // Mock download function
  const handleDownloadTemplate = () => {
    // In a real app, this would be a link to a static asset: /assets/rfi_template.xlsx
    alert('The RFI Template (v1.2) is now downloading...');
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
      {/* Download Template Section */}
      <Card className="mb-8 border-brand-accent/20 bg-brand-accent/5">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-brand-accent/20 shrink-0">
            <SafeIcon icon={FiFileText} className="text-3xl text-brand-accent" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-lg font-serif text-brand-text mb-1">Don't have the template yet?</h3>
            <p className="text-sm text-brand-muted mb-4 max-w-md">
              Download the official Writs & Giggles RFI workbook. It contains all the necessary fields and validation for a successful submission.
            </p>
            <Button 
              variant="outline" 
              onClick={handleDownloadTemplate}
              className="bg-white hover:bg-brand-accent hover:text-white transition-all group"
            >
              <SafeIcon icon={FiDownload} className="mr-2 group-hover:bounce" />
              Download RFI Template (.xlsx)
            </Button>
          </div>
        </div>
      </Card>

      {/* Upload Section */}
      <Card className="mb-8">
        <div className="mb-6">
          <h3 className="text-[10px] font-bold text-brand-muted uppercase tracking-widest mb-2">Step 2</h3>
          <h2 className="text-xl font-serif text-brand-text">Upload the completed workbook</h2>
          <p className="text-sm text-brand-muted mt-1">Only completed Writs & Giggles .xlsx workbooks are accepted.</p>
        </div>

        <div className="mb-4 text-xs font-semibold text-brand-muted uppercase tracking-wider">Completed Workbook</div>
        <div 
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          onClick={() => !data.upload.workbook && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-10 text-center transition-all 
            ${data.upload.workbook ? 'border-brand-success bg-brand-success/5 cursor-default' : 
              isDragging ? 'border-brand-accent bg-brand-accent/5 cursor-pointer' : 
              'border-brand-border bg-brand-light/30 hover:bg-brand-light/60 cursor-pointer'}`}
        >
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={(e) => handleFile(e.target.files[0])} 
            accept=".xlsx,.xls" 
            className="hidden" 
          />
          
          {data.upload.workbook ? (
            <div className="flex flex-col items-center">
              <SafeIcon icon={FiCheckCircle} className="text-4xl text-brand-success mb-4" />
              <p className="font-medium text-brand-text">{data.upload.workbook.name}</p>
              <p className="text-xs text-brand-muted mt-2">{(data.upload.workbook.size / 1024).toFixed(1)} KB</p>
              <Button 
                variant="ghost" 
                className="mt-4 text-xs" 
                onClick={(e) => { e.stopPropagation(); updateData('upload', 'workbook', null); }}
              >
                Remove file
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <SafeIcon icon={FiUploadCloud} className="text-4xl text-brand-muted mb-4" />
              <h3 className="text-lg font-serif text-brand-text mb-2">Choose workbook</h3>
              <p className="text-sm text-brand-muted mb-6">Drag the file here, or click to browse from your device.</p>
              <div className="flex gap-2">
                <span className="px-3 py-1 bg-white border border-brand-border rounded-full text-xs text-brand-muted">Accepted format: .xlsx</span>
                <span className="px-3 py-1 bg-white border border-brand-border rounded-full text-xs text-brand-muted">Completed W&G template</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 pt-8 border-t border-brand-border">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div>
              <h4 className="font-medium text-brand-text text-sm">Other supporting documents</h4>
              <p className="text-xs text-brand-muted mt-1">Optional supporting files such as contracts, receipts, and invoices.</p>
            </div>
            <Button variant="outline" className="text-xs whitespace-nowrap">Upload supporting documents</Button>
          </div>
          
          <p className="text-xs text-brand-muted italic mb-4">No supporting files added yet.</p>
          
          <div className="flex gap-2">
            <span className="px-3 py-1 bg-brand-light rounded-full text-xs text-brand-muted">Contracts</span>
            <span className="px-3 py-1 bg-brand-light rounded-full text-xs text-brand-muted">Receipts</span>
            <span className="px-3 py-1 bg-brand-light rounded-full text-xs text-brand-muted">Invoices</span>
          </div>
        </div>
      </Card>

      <Card>
        <div className="mb-6">
          <h3 className="text-[10px] font-bold text-brand-muted uppercase tracking-widest mb-2">Step 3</h3>
          <h2 className="text-xl font-serif text-brand-text">Submit the workbook</h2>
          <p className="text-sm text-brand-muted mt-1">When you submit, the workbook is parsed and the payload is sent immediately.</p>
        </div>
        
        <Button 
          onClick={submitForm} 
          disabled={!canSubmit} 
          className="w-full sm:w-auto px-10 h-12"
        >
          Submit Workbook
        </Button>
        
        {!canSubmit && (
          <div className="mt-4 flex items-start gap-2 text-brand-muted">
            <SafeIcon icon={FiInfo} className="text-xs mt-0.5" />
            <p className="text-xs">
              Still needed: <span className="font-bold text-brand-text">{missing.join(', ')}</span>.
            </p>
          </div>
        )}
      </Card>
    </motion.div>
  );
};